import { useMemo } from 'react'
import styled from 'styled-components'

import { premiumFontCss } from '@root/entities/premium/ui/constants'

import type { CurrencyModel } from '../../model'

/**
 * What stands in for a token that has no picture.
 *
 * One large initial rather than three small letters. Three set small read as a
 * cramped label and made a row look broken; a single letter filling the tile
 * reads as a deliberate placeholder, which is what axiom does and what a
 * scrolling column needs — the eye should be able to tell "no logo yet" from
 * "logo still loading" at a glance.
 *
 * The letter is sized from the tile rather than fixed, so the same component
 * serves a 16px row icon and a 64px card.
 */
export const CurrencyIconFallback = ({
  currency,
  premium,
}: {
  currency: CurrencyModel
  premium: boolean
}) => {
  const initial = useMemo(() => {
    // Tickers carry sigils and emoji: `$GOAT` should show G, not a dollar
    // sign. If a ticker is nothing but decoration, its first character is
    // still better than an empty tile.
    const letters = currency.ticker.replaceAll(/[^\p{Letter}\p{Number}]/gu, '')
    return (letters || currency.ticker).slice(0, 1).toUpperCase()
  }, [currency.ticker])

  return (
    <Fallback data-premium={premium}>
      <Initial data-premium={premium}>{initial}</Initial>
    </Fallback>
  )
}

const Fallback = styled.div`
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;

  width: 100%;
  height: 100%;

  border-radius: var(--currency-icon-inner-border-radius);

  background-color: var(--backgroundA-3);
`

const Initial = styled.div`
  font-size: calc(var(--currency-icon-size, 30px) * 0.58);
  font-weight: 500;
  line-height: 1;
  color: var(--text-5);
  user-select: none;

  ${Fallback} &[data-premium='true'] {
    ${premiumFontCss}
  }
`
